import { zodResolver } from "@hookform/resolvers/zod";
import { Box, MenuItem, Typography } from "@mui/material";
import { useConfirm } from "material-ui-confirm";
import { useSnackbar } from "notistack";
import { ReactElement, ReactNode } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { z } from "zod";

import {
  BasicFormInput,
  createSchemaForForm,
  MetadataFieldTypes,
  MetadataValueSchema,
  MetadataValueSchemaOutput,
  MetadataValueSchemaInput,
  MetadataUserListValueSchemaOutput,
  UserSchemaOutput,
} from "@stw/yup-validators";

import {
  EditCampaignBasicsMutation,
  EditCampaignBasicsMutationVariables,
  InboxType,
  IntegrationServiceType,
  NgpVanIntegration,
  ResetCampaignContactImportJobMutation,
  ResetCampaignContactImportJobMutationVariables,
} from "~frontend/__generated__/graphql";
import Loading from "~frontend/components/Loading";
import RouteLeavingGuard from "~frontend/components/RouteLeavingGuard";
import { EditMetadataValuesSection } from "~frontend/components/campaignMetadata/EditMetadataValues";
import {
  ApiMetadataFieldValue,
  getDefaultValueFromCampaignMetadata,
  getValueFromCampaignMetadata,
  mapValueTypenameToFieldType,
} from "~frontend/components/campaignMetadata/common";
import { submitErrorField } from "~frontend/components/forms";
import {
  RhfErrorBanner,
  RhfSelect,
  RhfTextField,
} from "~frontend/components/forms/react-hook-form-components";
import {
  EDIT_CAMPAIGN_BASICS,
  RESET_CAMPAIGN_CONTACT_UPLOAD_JOB,
} from "~frontend/graphql/campaigns";
import { useIntegrations } from "~frontend/hooks/useIntegrations";
import { SubaccountAdminData } from "~frontend/providers/LoggedInUserProvider/context";
import { useSubaccountAdminConfig } from "~frontend/providers/SubaccountProvider";
import { RhfSaveButtons } from "~frontend/routes/subaccounts/campaignEditor/campaignEditorUtils/SaveButtons";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import useMutation from "~frontend/util/useMutation";

import { NgpVanIntegrationDatabaseLabel } from "./LoadContacts/NgpVanImport";
import { StepInstructions } from "./StepInstructions";
import { TexterSettings } from "./TexterSettings";
import { CampaignForEditor } from "./types";

export type BasicValues = {
  [submitErrorField]?: string | null;
  name: string;
  description: string;
  integrationIdAndDbMode: string;
  inboxOverride: boolean;
  inboxId: string | null;
  textingHoursOverrideSubaccount: boolean;
  textingHoursTimezone: string;
  textingHoursStart: string;
  textingHoursEnd: string;
  texterAssignmentBatchSizeOverrideSubaccount: boolean;
  texterAssignmentBatchSize: number;
  texterAssignmentExpirationMinutesOverrideSubaccount: boolean;
  texterAssignmentExpirationMinutes: number;
  goToNext?: boolean;
};

type CampaignForBasic = Pick<
  CampaignForEditor,
  | "name"
  | "description"
  | "integration"
  | "integrationSettings"
  | "textingHoursTimezone"
  | "textingHoursStart"
  | "textingHoursEnd"
  | "inbox"
>;

function buildIntegrationIdAndDbMode(campaign: CampaignForBasic): string {
  return campaign.integration?.id && campaign.integrationSettings?.databaseMode
    ? [campaign.integration.id, campaign.integrationSettings.databaseMode].join(
        "|"
      )
    : "";
}

function getBasicsInitialValues(
  campaign: CampaignForBasic,
  subaccount: SubaccountAdminData
): BasicFormInput {
  const integrationIdAndDbMode = buildIntegrationIdAndDbMode(campaign);

  return {
    name: campaign.name,
    description: campaign.description || "",
    integrationIdAndDbMode,
    textingHoursOverrideSubaccount: !!(
      campaign.textingHoursTimezone &&
      campaign.textingHoursStart &&
      campaign.textingHoursEnd
    ),
    textingHoursTimezone:
      campaign.textingHoursTimezone || subaccount.textingHoursTimezone,
    textingHoursStart:
      campaign.textingHoursStart || subaccount.textingHoursStart,
    textingHoursEnd: campaign.textingHoursEnd || subaccount.textingHoursEnd,
    inboxOverride: campaign.inbox.type !== InboxType.Campaign,
    inboxId:
      campaign.inbox.type === InboxType.Campaign ? null : campaign.inbox.id,
    texterAssignmentBatchSizeOverrideSubaccount:
      !!campaign.inbox.texterAssignmentBatchSize,
    texterAssignmentBatchSize:
      campaign.inbox.texterAssignmentBatchSize ||
      subaccount.texterAssignmentBatchSize,
    texterAssignmentExpirationMinutesOverrideSubaccount:
      !!campaign.inbox.texterAssignmentExpirationMinutes,
    texterAssignmentExpirationMinutes:
      campaign.inbox.texterAssignmentExpirationMinutes ||
      subaccount.texterAssignmentExpirationMinutes,
  };
}

export function getMetadataInitialValues(
  apiFields: ApiMetadataFieldValue[]
): Record<string, MetadataValueSchemaOutput> {
  return Object.fromEntries(
    apiFields.map(field => {
      // The graphql api returns stringValue / boolValue / stringListValue.
      // We need to retrieve the correct `value` to parse using MetadataValueSchema.
      const value = getValueFromCampaignMetadata(field);
      const defaultValue = getDefaultValueFromCampaignMetadata(field);
      const type = mapValueTypenameToFieldType(field.__typename);
      const fieldData: Omit<MetadataValueSchemaInput, "type"> = {
        ...field,
        defaultValue,
        value,
      };
      // Convert using a zod schema so we can rely on the union/intersection
      // to create the appropriate form schema.
      const metadataValues = MetadataValueSchema.parse({
        ...fieldData,
        type,
      });
      return [field.name, metadataValues];
    })
  );
}

export function validateBasics({
  campaign,
}: {
  campaign: CampaignForEditor;
}): { title: string; message: ReactNode }[] {
  const errorFields: string[] = [];
  campaign.metadata.forEach(field => {
    const value = getValueFromCampaignMetadata(field);
    if (!field.required) {
      return;
    }
    switch (field.__typename) {
      case "CampaignMetadataBooleanValue": {
        if (value === null) {
          errorFields.push(field.name);
        }
        break;
      }
      case "CampaignMetadataStateMultiValue":
      case "CampaignMetadataEnumMultiValue": {
        if (!value || (value as string[]).length === 0) {
          errorFields.push(field.name);
        }
        break;
      }
      case "CampaignMetadataEnumValue":
      case "CampaignMetadataStateValue":
      case "CampaignMetadataStringValue": {
        if (!value) {
          errorFields.push(field.name);
        }
        break;
      }
      case "CampaignMetadataUserListValue": {
        const { userId } = value as UserSchemaOutput;
        if (!userId) {
          errorFields.push(field.name);
        }
        break;
      }
    }
  });
  if (errorFields.length === 0) {
    return [];
  }
  return [
    {
      title: "Basics incomplete",
      message:
        "You must complete the required metadata fields before starting your campaign.",
    },
  ];
}

export default function Basics({
  campaign,
  goToNextSection,
}: {
  campaign: CampaignForEditor;
  goToNextSection: (opts?: { bypassRouteLeavingGuard?: boolean }) => void;
}): ReactElement {
  const { enqueueSnackbar } = useSnackbar();
  const subaccount = useSubaccountAdminConfig()!;

  const confirm = useConfirm();

  const formatErrorMessage = useErrorMessageFormatter();
  const readOnly = !!campaign.startedAt;

  const {
    loading: integrationsLoading,
    hasIntegration,
    integrations,
  } = useIntegrations({
    service: IntegrationServiceType.Ngpvan,
  });

  const [editCampaignBasics] = useMutation<
    EditCampaignBasicsMutation,
    EditCampaignBasicsMutationVariables
  >(EDIT_CAMPAIGN_BASICS);

  const [resetCampaignContactImportJob] = useMutation<
    ResetCampaignContactImportJobMutation,
    ResetCampaignContactImportJobMutationVariables
  >(RESET_CAMPAIGN_CONTACT_UPLOAD_JOB);

  const showMetadataValuesSection = campaign.metadata.length > 0;
  const basicsInitialValues = getBasicsInitialValues(campaign, subaccount);
  const initialMetadataValues = getMetadataInitialValues(campaign.metadata);
  const ValidationSchema = createSchemaForForm(initialMetadataValues);
  type ValidationSchemaType = z.input<typeof ValidationSchema>;

  const formMethods = useForm({
    resolver: zodResolver(ValidationSchema),
    values: {
      ...basicsInitialValues,
      metadata: initialMetadataValues,
    },
  });

  const {
    control,
    reset,
    handleSubmit,
    formState: { isDirty },
  } = formMethods;

  function onSubmitMetadata(
    formData: ValidationSchemaType["metadata"]
  ): EditCampaignBasicsMutationVariables["input"]["metadataValues"] {
    return Object.entries(formData).map(([_, fieldValue]) => {
      const fieldType = fieldValue.type;
      const { value, id, fieldId } = fieldValue;
      let valuesForSubmit;
      if (
        fieldType === MetadataFieldTypes.STRING ||
        fieldType === MetadataFieldTypes.ENUM ||
        fieldType === MetadataFieldTypes.STATE
      ) {
        valuesForSubmit = { stringValue: value as string };
      } else if (fieldType === MetadataFieldTypes.BOOLEAN) {
        valuesForSubmit = { boolValue: value as boolean | null };
      } else if (
        fieldType === MetadataFieldTypes.ENUM_MULTI ||
        fieldType === MetadataFieldTypes.STATE_MULTI
      ) {
        valuesForSubmit = { stringListValue: value as string[] };
      } else if (fieldType === MetadataFieldTypes.USER_LIST) {
        valuesForSubmit = {
          uuidValue: (value as MetadataUserListValueSchemaOutput["value"])
            .userId,
        };
      }
      return {
        id,
        fieldId,
        ...valuesForSubmit,
      };
    });
  }

  const handleSave = async ({
    name,
    description,
    integrationIdAndDbMode,
    inboxOverride,
    inboxId,
    textingHoursOverrideSubaccount,
    textingHoursStart,
    textingHoursEnd,
    textingHoursTimezone,
    texterAssignmentBatchSizeOverrideSubaccount,
    texterAssignmentBatchSize,
    texterAssignmentExpirationMinutesOverrideSubaccount,
    texterAssignmentExpirationMinutes,
    goToNext,
    metadata,
  }: ValidationSchemaType): Promise<void> => {
    let integrationId = null;
    let integrationSettings = null;
    const integrationChanged =
      integrationIdAndDbMode != basicsInitialValues.integrationIdAndDbMode;

    if (
      integrationChanged &&
      (campaign.contactImportJob?.result?.nContacts ?? 0) > 0
    ) {
      const { confirmed } = await confirm({
        title: "Confirm clear contacts",
        description: "Are you sure? This will delete any existing contacts.",
      });
      if (!confirmed) {
        return;
      }

      await resetStagedCampaignContacts(campaign.id);
    }

    if (integrationIdAndDbMode) {
      const [splitIntegrationId, dbMode] = integrationIdAndDbMode.split("|");
      integrationId = splitIntegrationId;
      integrationSettings = { databaseMode: dbMode };
    }

    const metadataVariables = onSubmitMetadata(metadata);

    const { errors: basicsErrors, data: basicsData } = await editCampaignBasics(
      {
        variables: {
          input: {
            campaignId: campaign.id,
            name,
            description,
            integrationId,
            integrationSettings,
            inboxId: inboxOverride ? inboxId : null,
            metadataValues: metadataVariables,
            ...(textingHoursOverrideSubaccount
              ? {
                  textingHoursStart,
                  textingHoursEnd,
                  textingHoursTimezone,
                }
              : {}),
            ...(texterAssignmentBatchSizeOverrideSubaccount
              ? { texterAssignmentBatchSize }
              : {}),
            ...(texterAssignmentExpirationMinutesOverrideSubaccount
              ? { texterAssignmentExpirationMinutes }
              : {}),
          },
        },
      }
    );

    if (basicsErrors) {
      enqueueSnackbar("Failed to save campaign", { variant: "error" });
    } else {
      reset({
        ...getBasicsInitialValues(
          {
            name: basicsData!.editCampaignBasics.name,
            description: basicsData!.editCampaignBasics.description,
            integration: basicsData!.editCampaignBasics.integration,
            integrationSettings:
              basicsData!.editCampaignBasics.integrationSettings,
            textingHoursStart: basicsData!.editCampaignBasics.textingHoursStart,
            textingHoursEnd: basicsData!.editCampaignBasics.textingHoursEnd,
            textingHoursTimezone:
              basicsData!.editCampaignBasics.textingHoursTimezone,
            inbox: basicsData!.editCampaignBasics.inbox,
          },
          subaccount
        ),
        metadata: getMetadataInitialValues(
          basicsData!.editCampaignBasics.metadata
        ),
      });

      if (goToNext) {
        goToNextSection({ bypassRouteLeavingGuard: true });
      } else {
        enqueueSnackbar("Saved campaign", { variant: "info" });
      }
    }
  };

  async function resetStagedCampaignContacts(
    campaignId: string
  ): Promise<void> {
    const { errors } = await resetCampaignContactImportJob({
      variables: {
        input: {
          campaignId,
        },
      },
    });

    if (errors) {
      enqueueSnackbar(
        `Failed to reset contacts: ${formatErrorMessage(errors)}`
      );
    }
  }

  if (integrationsLoading) {
    return <Loading />;
  }

  const integrationDatabaseTuples = (
    integrations as NgpVanIntegration[]
  ).reduce(
    (acc: NgpVanIntegrationDatabaseLabel[], integration: NgpVanIntegration) => {
      if (integration.metadata.hasMyCampaign) {
        acc.push({
          id: [integration.id, "MY_CAMPAIGN"].join("|"),
          label: `${integration.name} > MyCampaign / EveryAction`,
        });
      }
      if (integration.metadata.hasMyVoters) {
        acc.push({
          id: [integration.id, "MY_VOTERS"].join("|"),
          label: `${integration.name} > MyVoters`,
        });
      }
      return acc;
    },
    [] as NgpVanIntegrationDatabaseLabel[]
  );

  return (
    <>
      <Box sx={{ pb: 3 }}>
        <StepInstructions header="Basics">
          What's your campaign about? Set your campaign basics below.
        </StepInstructions>
      </Box>

      <FormProvider {...formMethods}>
        <form>
          <RouteLeavingGuard when={isDirty} />
          <RhfErrorBanner control={control} />
          <RhfTextField control={control} fullWidth label="Title" name="name" />
          <RhfTextField
            control={control}
            fullWidth
            multiline
            label="Description (optional)"
            name="description"
          />

          {hasIntegration && (
            <>
              <Typography
                variant="display0Bold"
                sx={{ pb: 1, pt: 3 }}
                component="div"
              >
                Integrations
              </Typography>
              <Typography variant="h2" sx={{ pb: 2 }}>
                Select an NGP VAN / EveryAction integration to connect with for
                importing contacts or mapping survey responses.
              </Typography>
              <RhfSelect
                name="integrationIdAndDbMode"
                disabled={readOnly}
                fullWidth
                label="Integration"
                withNoneOption
              >
                {integrationDatabaseTuples.map(integrationDbTuple => (
                  <MenuItem
                    value={integrationDbTuple.id}
                    key={integrationDbTuple.id}
                  >
                    {integrationDbTuple.label}
                  </MenuItem>
                ))}
              </RhfSelect>
            </>
          )}

          {showMetadataValuesSection && (
            <EditMetadataValuesSection
              campaignId={campaign.id}
              apiFields={initialMetadataValues}
            />
          )}

          <TexterSettings readOnly={readOnly} />

          <RhfSaveButtons
            continueWithoutSaving={goToNextSection}
            onSubmit={handleSubmit(handleSave)}
          />
        </form>
      </FormProvider>
    </>
  );
}
